import React, { ReactElement } from "react";
import { GetStaticProps } from "next";
import QueryString from "qs";
import Home from "@/Components/pages/Home";
import { useDevice } from "@/hooks/use-device";
import { fetchWithRetry } from "@/utils/fetchWithRetry";

interface HomePageProps {
  blocksData: Array<unknown>; // TODO: Define proper type
  seoData: unknown; // TODO: Define proper type
  isNew: boolean;
}

const baseUrl = `${process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL}`;
// Bump NEXT_PUBLIC_HOME_PAGE_VERSION whenever the homepage content/structure
// changes, so the query string changes and Next's fetch data cache (and any
// upstream/CDN cache keyed on the URL) treats it as a fresh request instead
// of serving a stale response for the remainder of the ISR window.
const homePageVersion = process.env.NEXT_PUBLIC_HOME_PAGE_VERSION || "1";

/**
 * Static Site Generation with Incremental Static Regeneration
 * This pre-renders the page at build time and revalidates every 60 seconds
 */
export const getStaticProps: GetStaticProps<HomePageProps> = async () => {
  // Build query to fetch "home" page content
  const query = QueryString.stringify(
    {
      filters: {
        slug: {
          $eq: "home",
        },
      },
      populate: "deep,8",
    },
    { encodeValuesOnly: false },
  );

  const url = `${baseUrl}pages-v2s?${decodeURIComponent(query)}&v=${homePageVersion}`;

  // Transient network failures are retried rather than swallowed: returning
  // `notFound` on a timeout used to let a build finish "successfully" while
  // publishing a 404 homepage.
  const response = await fetchWithRetry(url, {
    // Cache for build-time fetching
    next: { revalidate: 60 },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch homepage data: API responded with status ${response.status}`,
    );
  }

  const data = await response.json();

  const listData = data?.data?.[0]?.attributes;

  if (!listData?.blocks) {
    return { notFound: true, revalidate: 10 };
  }

  return {
    props: {
      blocksData: listData.blocks,
      seoData: listData.seo ?? null,
      isNew: true,
    },
    // Revalidate every 60 seconds (ISR)
    revalidate: 60,
  };
};

const HomePage = ({ blocksData, seoData }: HomePageProps): ReactElement => {
  // Client-side device detection
  const deviceInfo = useDevice();
  const isMobile = deviceInfo.isMobile;

  // console.log("HomePage: blocksData:", blocksData);

  return (
    <>
      <Home blocks={blocksData} seo={seoData} isMobile={isMobile} />
    </>
  );
};

export default HomePage;
